/* eslint-disable camelcase */
import { camelize } from 'humps';
import qs from 'qs';
import { keys, pick, toPairs } from 'ramda';

import type {
  CamelizedQueryStrings,
  QueryStringEntry,
  QueryStringKey,
  QueryStrings,
  QueryStringValueType,
} from './queryParams.types';

// This is a workaround for picking only the first element in case of an array
// qs.parse returns an array if the query string is repeated
const firstValue = (value: QueryStringValueType) => (Array.isArray(value) ? value[0] : value) as string;

const asNumber = (value: QueryStringValueType) => {
  const result = firstValue(value).replace(/\D/g, '');
  if (!result) return -1;

  return Number(result);
};

const asString = (value: QueryStringValueType) => `${firstValue(value)}`;

const QUERY_STRING: QueryStrings = {
  game_id: asNumber,
  identifier: asString,
  language: asString,
  limit_id: asNumber,
  operatorId: asNumber,
  table_id: asNumber,
  token: asString,
  playerId: asString,
  uid: asNumber,
  skin: asString,
  skipSeatReservation: asNumber,
  isEvoWrapper: asNumber,
} as const;

export const getParsedQueryString = (
  locationSearch: string | null = null
) => qs.parse(locationSearch || window.location.search, { ignoreQueryPrefix: true });

const getAllowedQueryString = (
  locationSearch: string | null = null
) => pick(keys(QUERY_STRING), getParsedQueryString(locationSearch));

const getQueryParams = (
  locationSearch: string | null = null
): CamelizedQueryStrings => {
  const paramsPairs = toPairs(getAllowedQueryString(locationSearch)) as QueryStringEntry[];

  const camelizedParams = (params: CamelizedQueryStrings, [ key, value ]: QueryStringEntry) => {
    (params[camelize(key)] as unknown) = QUERY_STRING[key]?.(value);
    return params;
  };

  return paramsPairs.reduce(camelizedParams, {} as CamelizedQueryStrings);
};

const containsQueryParams = (props: QueryStringKey[]) => props.every((prop) => getParsedQueryString()[prop]);

const toStringifiedQuery = () => qs.stringify(getAllowedQueryString(), { addQueryPrefix: true });

export const queryParams = {
  getQueryParams,
  containsQueryParams,
  toStringifiedQuery,
};
